import { Lock } from 'lucide-react'
import type { DomainFieldDef } from '@/lib/domainSchema'
import { isFieldFilled } from '@/lib/domainSchema'

interface TruthFieldRowProps {
  field: DomainFieldDef
  value: unknown
}

/**
 * One labelled sub-field row inside a TruthCard. The value is rendered
 * read-only — source-of-truth is backend-generated and inline edit is
 * deferred until the `PATCH /context/source-of-truth` semantic is locked
 * (Vidit blocker). The lock affordance + tooltip ("Editing arrives next")
 * tells the user this is intentional, not missing.
 *
 * Empty values render the muted placeholder "Not yet captured" so the
 * row never collapses to zero-height.
 */
export function TruthFieldRow({ field, value }: TruthFieldRowProps) {
  const filled = isFieldFilled(value)

  return (
    <div
      data-testid={`truth-field-${field.key}`}
      className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2 md:gap-4 items-start py-3 border-b border-border-default last:border-b-0"
    >
      <div className="text-xs font-medium uppercase tracking-wider text-text-tertiary md:pt-0.5">
        {field.label}
      </div>
      <div className="text-sm text-text-primary leading-relaxed min-w-0">
        {filled ? (
          <FieldValue field={field} value={value} />
        ) : (
          <span className="text-text-tertiary italic">Not yet captured</span>
        )}
      </div>
      <span
        data-testid={`truth-field-lock-${field.key}`}
        title="Editing arrives next"
        aria-label="Read-only — editing arrives next"
        className="hidden md:inline-flex items-center text-text-tertiary"
      >
        <Lock size={12} />
      </span>
    </div>
  )
}

/** Extract a human-readable label from a structured object. */
function objectLabel(obj: Record<string, unknown>): string {
  if (typeof obj.name === 'string') {
    const suffix = obj.type || obj.role || obj.title
    return suffix ? `${obj.name} (${suffix})` : obj.name
  }
  if (typeof obj.title === 'string') {
    const nameLike = typeof obj.name === 'string' ? obj.name : null
    return nameLike ? `${nameLike} — ${obj.title}` : obj.title
  }
  if (typeof obj.category === 'string') {
    const amount = typeof obj.amount === 'string' ? obj.amount : null
    return amount ? `${obj.category} — ${amount}` : obj.category
  }
  for (const v of Object.values(obj)) {
    if (typeof v === 'string') return v
  }
  return JSON.stringify(obj)
}

function FieldValue({ field, value }: TruthFieldRowProps) {
  if (field.kind === 'stringArray') {
    const items = (Array.isArray(value) ? value : [])
      .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    return (
      <ul className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <li
            key={`${field.key}-${i}`}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs text-text-secondary bg-bg-elevated border border-border-default"
          >
            {item}
          </li>
        ))}
      </ul>
    )
  }

  if (field.kind === 'objectArray') {
    const items = Array.isArray(value) ? value : []
    if (items.length === 0) return <span className="text-text-tertiary italic">Not yet captured</span>
    return (
      <ul className="flex flex-wrap gap-1.5">
        {items.map((item, i) => {
          if (typeof item !== 'object' || item == null) return null
          return (
            <li
              key={`${field.key}-${i}`}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs text-text-secondary bg-bg-elevated border border-border-default"
            >
              {objectLabel(item as Record<string, unknown>)}
            </li>
          )
        })}
      </ul>
    )
  }

  if (field.kind === 'object') {
    if (typeof value !== 'object' || value == null || Array.isArray(value)) {
      return <span className="text-text-tertiary italic">Not yet captured</span>
    }
    const obj = value as Record<string, unknown>

    // MarketSize (TAM / SAM / SOM) — render as labeled pills
    if (field.key === 'marketSize') {
      const tam = typeof obj.tam === 'string' && obj.tam ? obj.tam : null
      const sam = typeof obj.sam === 'string' && obj.sam ? obj.sam : null
      const som = typeof obj.som === 'string' && obj.som ? obj.som : null
      const items = [{ label: 'TAM', value: tam }, { label: 'SAM', value: sam }, { label: 'SOM', value: som }].filter(x => x.value)
      if (items.length === 0) return <span className="text-text-tertiary italic">Not yet captured</span>
      return (
        <ul className="flex flex-wrap gap-1.5">
          {items.map(item => (
            <li
              key={item.label}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-bg-elevated border border-border-default"
            >
              <span className="text-text-tertiary mr-1">{item.label}:</span>
              <span className="font-medium">{item.value}</span>
            </li>
          ))}
        </ul>
      )
    }

    const name = typeof obj.name === 'string' ? obj.name : null
    const title = typeof obj.title === 'string' ? obj.title : null
    const role = typeof obj.role === 'string' ? obj.role : null
    const demographics = obj.demographics as Record<string, unknown> | undefined

    return (
      <div className="space-y-1">
        {name && (
          <span className="font-medium">
            {name}
            {title && <span className="text-text-tertiary"> — {title}</span>}
            {role && <span className="text-text-tertiary"> ({role})</span>}
          </span>
        )}
        {demographics && (
          <div className="text-xs text-text-tertiary">
            {Object.entries(demographics)
              .filter(([, v]) => typeof v === 'string' && v)
              .map(([k, v]) => `${k}: ${v}`)
              .join(' · ')}
          </div>
        )}
      </div>
    )
  }

  const text = typeof value === 'string' ? value : String(value ?? '')
  return (
    <p className="whitespace-pre-wrap break-words">
      {text}
    </p>
  )
}
